import _ from 'lodash'

import VALUES from 'core/data-model/constants/values'
import {
  getDiscount,
  validateTotalPremium,
  calculateMRTASumAssuredContract,
  calculateGLTSPSumAssuredContract,
} from 'core/service/basic-plan'
import { transformOccupationFactor } from 'core/service/insured/occupation'
import { isCurrentDateInRange } from 'core/service/lib/date'
import { isNotNil } from 'core/service/lib/type-check'
import {
  hasRidersNeedToCalculateFromTotalPremiumOrAnnualPremium,
  hasRidersNeedToCalculateFromAnnualTotalPremium,
} from 'core/service/rider'
import { calculateRiderPremiumSelectedPlanGroup } from 'core/service/rider/premium-calculation'
import { isInvestment } from 'identity/fse-branch/services'
import { getToggles } from 'quick-quote/feature-toggles'
import { isMRTAGroup, isGLTSPGroup } from 'quick-quote/product-mrta/coverage-plan/selectors'

import { getDiscountByOccupationFactor } from './discount'
import { calculateTotalPremium, totalRiderPremium } from './premium'

export const getDefaultSumAssured = (basicPlan) => {
  return isNaN(basicPlan.defaultSumAssured) ? basicPlan.sumAssuredMinimum : basicPlan.defaultSumAssured
}

export const getActiveBasicPlanCampaignDiscount = (campaignDiscount) => {
  return isCurrentDateInRange(campaignDiscount.startDate, campaignDiscount.endDate) ? campaignDiscount : {}
}

export const getFactor = (basicPlan, modelFactor) => {
  return isInvestment(basicPlan.category) ? modelFactor.factorILP : modelFactor.factor
}

export const isCheckBasicCampaignDiscountActive = ({ basicPlan, occupationFactor, userAgentType }) => {
  const campaignDiscount = _.get(basicPlan, 'campaignDiscount')
  if (campaignDiscount) {
    const isBasicCampaignDiscountDateRangeActive = isCurrentDateInRange(
      _.get(campaignDiscount, 'startDate'),
      _.get(campaignDiscount, 'endDate')
    )

    // prettier-ignore
    const isBasicCampaignDiscountOccupationFactorActive =
      campaignDiscount.validBasicOccupationFactors && occupationFactor
        ? campaignDiscount.validBasicOccupationFactors.map(transformOccupationFactor).includes(Number(occupationFactor))
        : true

    const isBasicCampaignDiscountAllowUserTypeActive =
      campaignDiscount.allowUserType && userAgentType ? campaignDiscount.allowUserType.includes(userAgentType) : true

    return (
      isBasicCampaignDiscountDateRangeActive &&
      isBasicCampaignDiscountOccupationFactorActive &&
      isBasicCampaignDiscountAllowUserTypeActive
    )
  } else {
    return false
  }
}

export const checkWaiveAnnualBasicPremiumForReplaceWording = (policyValue, basicPlan) => {
  const waivePremiumAtYear = _.get(basicPlan, 'campaignDiscount.waivePremiumAtYear', [])
  const annualBasicPremium = waivePremiumAtYear.includes(policyValue.yearEndPolicy)
    ? 'ยกเว้นเบี้ยฯ'
    : policyValue.annualBasicPremium
  return annualBasicPremium
}

/**
 * @param dataWrapper {ReturnType<import('core/data/data-wrapper')._DB>}
 * @param displayProductService {ReturnType<import('core/service/display-product/display-product').createDisplayProductService>}
 * @param insuredOccupationService {ReturnType<import('core/service/insured/occupation').createInsuredOccupationService>}
 * @param riderService {Awaited<ReturnType<import('core/service/rider/rider').createRiderService>>}
 * @param basicPlanPremiumService {ReturnType<import('core/service/basic-plan/premium').createBasicPlanPremiumService>}
 * @param basicPlanValidationService {ReturnType<import('core/service/basic-plan/validation').createBasicPlanValidationService>}
 * @param riderPremiumRateService {ReturnType<import('core/service/rider/premium-rate').createRiderPremiumRateService>}
 */
export function createBasicPlanService(
  dataWrapper,
  displayProductService,
  insuredOccupationService,
  riderService,
  basicPlanPremiumService,
  basicPlanValidationService,
  riderPremiumRateService
) {
  const getBasicPlanCampaignDiscountRate = (query, occupationFactor, userAgentType) =>
    getBasicPlanCampaignDiscount(query, occupationFactor, userAgentType).then((campaignDiscount) => {
      return campaignDiscount.value ? campaignDiscount.value : 0
    })

  const _getBasicPlanDiscountRates = (getBasicPlan, query) =>
    getBasicPlan(query).then((basicPlan) => basicPlan.discountRates)

  const getBasicPlanDiscountRates = (query) => _getBasicPlanDiscountRates(displayProductService.getBasicPlan, query)

  const _getDefaultSelectedRiders = (getBasicPlan, query) =>
    getBasicPlan(query).then((basicPlan) => basicPlan.selectedRiders)

  const getDefaultSelectedRiders = (query) => _getDefaultSelectedRiders(displayProductService.getBasicPlan, query)

  const getBasicPlanCampaignDiscount = (query, occupationFactor, userAgentType) =>
    displayProductService.getBasicPlan(query).then((basicPlan) => {
      return isCheckBasicCampaignDiscountActive({ basicPlan, occupationFactor, userAgentType })
        ? basicPlan.campaignDiscount
        : {}
    })

  const _getBasicPlanDiscountRate = (getBasicPlan, query, sumAssured, occupationFactor) =>
    getBasicPlan(query).then((basicPlan) => {
      return getDiscountByOccupationFactor({
        occupationFactor,
        discount: getDiscount(basicPlan.discountRates, sumAssured),
      })
    })

  const getBasicPlanDiscountRate = (query, sumAssured, occupationFactor) =>
    _getBasicPlanDiscountRate(displayProductService.getBasicPlan, query, sumAssured, occupationFactor)

  const getBasicPlanRateUnit = async (query) => {
    const basicPlan = await displayProductService.getBasicPlan(query)
    return basicPlan ? basicPlan.rateUnit : 0
  }

  const calculateCoveragePlanFromBasicPlanChange = async (props) => {
    const {
      displayProductQuery,
      natureOfDutyCodes,
      payerRelation,
      loanPeriod,
      coveragePeriod,
      loanAmount,
      interestRate,
      loan,
    } = props
    const basicPlan = await displayProductService.getBasicPlan(displayProductQuery)
    const sumAssured = getDefaultSumAssured(basicPlan)

    const validatedSumAssured = await basicPlanValidationService.validateSumAssured(
      displayProductQuery,
      natureOfDutyCodes,
      sumAssured,
      loanPeriod,
      coveragePeriod,
      loanAmount,
      interestRate,
      loan
    )
    return submitCoveragePlanPremiumCalculation(props, basicPlan, validatedSumAssured, payerRelation)
  }

  const calculateCoveragePlanFromSumAssured = async (props) => {
    let {
      sumAssured,
      displayProductQuery,
      natureOfDutyCodes,
      payerRelation,
      loanPeriod,
      coveragePeriod,
      loanAmount,
      interestRate,
      loan,
      insured,
      modelFactorID,
      availableRiders,
    } = props

    const basicPlan = await displayProductService.getBasicPlan(displayProductQuery)

    let sumAssuredContract = 0
    if (basicPlan.category === VALUES.MRTA) {
      const basicPremiumRate = await basicPlanPremiumService.lookUpBasicPremiumRateMRTA(
        basicPlan.productCode,
        insured.gender,
        insured.age,
        displayProductQuery.productPlanCode
      )
      const riderPremiumRate = isMRTAGroup(displayProductQuery.code)
        ? await basicPlanPremiumService.lookUpRiderPremiumRateMRTA(availableRiders, insured.gender, insured.age)
        : 0

      if (isMRTAGroup(displayProductQuery.code)) {
        sumAssuredContract = await calculateMRTASumAssuredContract({
          sumAssured,
          premiumRate: basicPremiumRate,
          loan,
          riderPremiumRate,
        })
      }

      if (isGLTSPGroup(displayProductQuery.code)) {
        const rateUnit = _.get(basicPlan, 'rateUnit', 0)
        const modelFactor = await dataWrapper.getModelFactorById(modelFactorID)
        const occupationFactor = await insuredOccupationService.getRiskiestOccupation(
          displayProductQuery,
          natureOfDutyCodes
        )
        const discount = await getBasicPlanDiscountRate(displayProductQuery, sumAssured, occupationFactor)
        const { basicPremium } = await calculateBasicPremiumAndAnnualBasicPremium({
          displayProductQuery,
          sumAssured,
          natureOfDutyCodes,
          premiumRate: basicPremiumRate,
          discount,
          rateUnit,
          modelFactor: modelFactor.factor,
          loan,
          riderPremiumRate,
        })

        sumAssuredContract = await calculateGLTSPSumAssuredContract({
          sumAssured,
          basicPremium: basicPremium,
          loan,
        })
      }
    }

    let validatedSumAssured = await basicPlanValidationService.validateSumAssured(
      displayProductQuery,
      natureOfDutyCodes,
      sumAssured,
      loanPeriod,
      coveragePeriod,
      loanAmount,
      interestRate,
      loan,
      sumAssuredContract
    )
    return submitCoveragePlanPremiumCalculation(props, basicPlan, validatedSumAssured, payerRelation)
  }

  const calculateCoveragePlanFromPremium = async (props) => {
    const {
      basicPremium,
      displayProductQuery,
      insured,
      natureOfDutyCodes,
      modelFactorID,
      payerRelation,
      loanPeriod,
      coveragePeriod,
      loanAmount,
      loan,
      availableRiders,
      interestRate,
    } = props
    const basicPlan = await displayProductService.getBasicPlan(displayProductQuery)
    const basicPremiumRate =
      isMRTAGroup(displayProductQuery.code) || isGLTSPGroup(displayProductQuery.code)
        ? await basicPlanPremiumService.lookUpBasicPremiumRateMRTA(
            basicPlan.productCode,
            insured.gender,
            insured.age,
            displayProductQuery.productPlanCode
          )
        : await basicPlanPremiumService.lookUpBasicPremiumRate(basicPlan.productCode, insured.gender, insured.age)
    const riderPremiumRate = isMRTAGroup(displayProductQuery.code)
      ? await basicPlanPremiumService.lookUpRiderPremiumRateMRTA(availableRiders, insured.gender, insured.age)
      : 0
    const discountRates = await getBasicPlanDiscountRates(displayProductQuery)
    const occupationFactor = await insuredOccupationService.getRiskiestOccupation(
      displayProductQuery,
      natureOfDutyCodes
    )
    let discount = await getBasicPlanDiscountRate(
      displayProductQuery,
      getDefaultSumAssured(basicPlan),
      occupationFactor
    )
    const rateUnit = await getBasicPlanRateUnit(displayProductQuery)
    const modelFactor = await dataWrapper.getModelFactorById(modelFactorID)
    const userAgentType = _.get(props, 'userAgentType')
    let campaignDiscount = await getBasicPlanCampaignDiscountRate(displayProductQuery, occupationFactor, userAgentType)
    const basicSumAssured = await basicPlanPremiumService.calculateBasicSumAssured(
      {
        displayProductQuery,
        premium: basicPremium,
        natureOfDutyCodes,
        premiumRate: basicPremiumRate,
        rateUnit,
        discount,
        modelFactor: modelFactor.factor,
        loan,
        riderPremiumRate,
      },
      discountRates,
      campaignDiscount
    )

    const validatedSumAssured = await basicPlanValidationService.validateSumAssured(
      displayProductQuery,
      natureOfDutyCodes,
      basicSumAssured,
      loanPeriod,
      coveragePeriod,
      loanAmount,
      interestRate,
      loan
    )
    return submitCoveragePlanPremiumCalculation(props, basicPlan, validatedSumAssured, payerRelation)
  }

  const populateInitialCoveragePlan = async (props) => {
    const {
      displayProductQuery,
      natureOfDutyCodes,
      userAgentType,
      insured,
      payerRelation,
      loanPeriod,
      coveragePeriod,
      loanAmount,
      interestRate,
      loan,
      allowOptionalIHealthyUltra,
    } = props
    const userLicenses = props.userLicenses || []
    const basicPlan = await displayProductService.getBasicPlan(displayProductQuery)

    const sumAssured = getDefaultSumAssured(basicPlan)
    let availableRiders = basicPlan.riders.map((rider) => ({
      ...rider,
      isSelected: basicPlan.selectedRiders.indexOf(rider.code) > -1,
      sumAssured: 0,
      premium: 0,
      selectedPlan: riderService.getSelectedRiderPlan(
        rider,
        undefined,
        basicPlan,
        sumAssured,
        userAgentType,
        undefined,
        undefined,
        insured.age
      ),
      isSelectable: true,
      yearsOfCoverage: 0,
      yearsOfPayment: 0,
      errors: [],
    }))

    if (!getToggles().ENABLE_RIDER_PB) {
      availableRiders = availableRiders.filter((rider) => !VALUES.RIDER_PB.includes(rider.code))
    }
    if (!getToggles().ENABLE_RIDER_NEW_WP) {
      availableRiders = availableRiders.filter((rider) => ![VALUES.WPTPD, VALUES.WPTPDCI].includes(rider.code))
    }
    if (!getToggles().ENABLE_RIDER_MCI) {
      availableRiders = availableRiders.filter((rider) => rider.code !== VALUES.MCI)
    }

    availableRiders = availableRiders.filter(
      (rider) => !rider.hasOwnProperty('allowUserType') || rider.allowUserType.some((v) => userAgentType === v)
    )

    if (getToggles().ENABLE_LICENSE_RIDER) {
      availableRiders = availableRiders.filter(
        (rider) => !rider.hasOwnProperty('licenses') || rider.licenses.every((v) => userLicenses.includes(v))
      )
    }
    if (getToggles().ENABLE_OPTIONAL_IHEALTHY_ULTRA) {
      var foundIndex = availableRiders.findIndex((rider) => rider.code == VALUES.MHP)
      if (foundIndex >= 0) {
        var updateSelectedPlan = availableRiders[foundIndex].selectedPlan
        updateSelectedPlan = {
          ...updateSelectedPlan,
          allowOptionalIHealthyUltra: allowOptionalIHealthyUltra ? allowOptionalIHealthyUltra : false,
        }
        availableRiders[foundIndex].selectedPlan = updateSelectedPlan
      }
    }
    const validatedSumAssured = await basicPlanValidationService.validateSumAssured(
      displayProductQuery,
      natureOfDutyCodes,
      sumAssured,
      loanPeriod,
      coveragePeriod,
      loanAmount,
      interestRate,
      loan
    )
    return submitCoveragePlanPremiumCalculation(
      { ...props, availableRiders },
      basicPlan,
      validatedSumAssured,
      payerRelation
    )
  }

  const submitCoveragePlanPremiumCalculation = async (props, basicPlan, validatedSumAssured) => {
    const { displayProductQuery, insured, modelFactorID, natureOfDutyCodes, payerRelation, availableRiders } = props
    const occupationFactor = await insuredOccupationService.getRiskiestOccupation(
      displayProductQuery,
      natureOfDutyCodes
    )
    const discount = await getBasicPlanDiscountRate(displayProductQuery, validatedSumAssured.value, occupationFactor)
    const basicPremiumRate =
      isMRTAGroup(displayProductQuery.code) || isGLTSPGroup(displayProductQuery.code)
        ? await basicPlanPremiumService.lookUpBasicPremiumRateMRTA(
            basicPlan.productCode,
            insured.gender,
            insured.age,
            displayProductQuery.productPlanCode
          )
        : await basicPlanPremiumService.lookUpBasicPremiumRate(basicPlan.productCode, insured.gender, insured.age)

    const riderPremiumRate = isMRTAGroup(displayProductQuery.code)
      ? await basicPlanPremiumService.lookUpRiderPremiumRateMRTA(availableRiders, insured.gender, insured.age)
      : 0

    const rateUnit = _.get(basicPlan, 'rateUnit', 0)
    const modelFactor = await dataWrapper.getModelFactorById(modelFactorID)
    const userAgentType = _.get(props, 'userAgentType')
    const campaignDiscount = await getBasicPlanCampaignDiscountRate(
      displayProductQuery,
      occupationFactor,
      userAgentType
    )
    const basicPlanCampaignDiscount = await getBasicPlanCampaignDiscount(
      displayProductQuery,
      occupationFactor,
      userAgentType
    )
    let campaignDiscountMessage = []
    if (basicPlanCampaignDiscount.campaignDiscountMessage) {
      campaignDiscountMessage = basicPlanCampaignDiscount.campaignDiscountMessage
    }
    let campaignDiscountEndDate = ''
    if (basicPlanCampaignDiscount.endDate) {
      campaignDiscountEndDate = basicPlanCampaignDiscount.endDate
    }
    return calculateCoveragePlanPremium(
      props,
      validatedSumAssured,
      discount,
      basicPremiumRate,
      rateUnit,
      modelFactor,
      basicPlan,
      payerRelation,
      riderPremiumRate,
      campaignDiscount,
      campaignDiscountMessage,
      campaignDiscountEndDate
    )
  }

  const calculateCoveragePlanPremium = async (
    props,
    validatedSumAssured,
    discount,
    basicPremiumRate,
    rateUnit,
    modelFactor,
    basicPlan,
    payerRelation,
    riderPremiumRate,
    campaignDiscount,
    campaignDiscountMessage,
    campaignDiscountEndDate
  ) => {
    const {
      displayProductQuery,
      insured,
      natureOfDutyCodes,
      payerNatureOfDutyCodes,
      modelFactorID,
      availableRiders,
      payerAge,
      payerGender,
      userAgentType,
      loan,
    } = props
    const {
      basicPremium,
      annualBasicPremium,
      fullBasicPremium,
      fullAnnualBasicPremium,
    } = await calculateBasicPremiumAndAnnualBasicPremium({
      displayProductQuery,
      sumAssured: validatedSumAssured.value,
      natureOfDutyCodes,
      premiumRate: basicPremiumRate,
      discount,
      rateUnit,
      modelFactor: modelFactor.factor,
      loan,
      riderPremiumRate,
      campaignDiscount,
    })

    // add additional value for yearly MRTA
    let sumAssuredContract
    if (isMRTAGroup(displayProductQuery.code)) {
      sumAssuredContract = await calculateMRTASumAssuredContract({
        sumAssured: validatedSumAssured.value,
        premiumRate: basicPremiumRate,
        loan,
        riderPremiumRate,
      })
    }

    if (isGLTSPGroup(displayProductQuery.code)) {
      sumAssuredContract = await calculateGLTSPSumAssuredContract({
        sumAssured: validatedSumAssured.value,
        basicPremium: basicPremium,
        loan,
      })
    }

    const accurateRiders = await riderService.getAccurateRiders({
      riders: availableRiders,
      basicSumAssured: validatedSumAssured.value,
      displayProductQuery,
      natureOfDutyCodes: natureOfDutyCodes,
      payerNatureOfDutyCodes: payerNatureOfDutyCodes,
      userAgentType,
      insured,
      payerAge,
    })

    const riderPremiumRates = await riderPremiumRateService.getRiderPremiumRates(
      insured.age,
      payerAge,
      insured.gender,
      payerGender,
      accurateRiders,
      natureOfDutyCodes,
      displayProductQuery
    )
    const riderOccupationFactors = await insuredOccupationService.getRiskiestOccupationFactorForRiders(
      displayProductQuery,
      natureOfDutyCodes,
      accurateRiders
    )
    const riderPayerOccupationFactors = await insuredOccupationService.getRiskiestOccupationFactorForRiders(
      displayProductQuery,
      payerNatureOfDutyCodes,
      accurateRiders
    )
    // IT will set SA for it should refactor to include to calculateSumAssuredAndPremiumForSelectedRiders
    const updatedSARiders = await getUpdatedRidersSAFromAnnualBasicPremium({
      riders: accurateRiders,
      displayProductQuery,
      sumAssured: validatedSumAssured.value,
      natureOfDutyCodes,
      premiumRate: basicPremiumRate,
      discount,
      rateUnit,
      modelFactor: modelFactor,
      annualBasicPremium,
      loan,
      campaignDiscount,
    })

    const updatedRiders = calculateSumAssuredAndPremiumForSelectedRiders({
      riders: updatedSARiders,
      validatedSumAssured,
      basicPremium,
      annualBasicPremium,
      riderPremiumRates,
      riderOccupationFactors,
      modelFactor,
      basicPlan,
      userAgentType,
      riderPayerOccupationFactors,
      loan,
      sumAssuredContract,
    })

    const validatedRiders = await riderService.validateRiders(
      updatedRiders,
      insured,
      payerAge,
      validatedSumAssured.value,
      natureOfDutyCodes,
      payerNatureOfDutyCodes,
      displayProductQuery,
      payerRelation,
      loan,
      userAgentType,
      modelFactor
    )
    const riderPremium = totalRiderPremium(validatedRiders)
    const totalPremium = calculateTotalPremium(basicPremium, riderPremium)

    return {
      totalPremium: validateTotalPremium({ basicSumAssured: validatedSumAssured.value, modelFactorID }, totalPremium),
      riderPremium: riderPremium,
      validatedRiders,
      basicPremium,
      validatedSumAssured,
      sumAssuredContract,
      fullBasicPremium,
      fullAnnualBasicPremium,
      campaignDiscountMessage,
      campaignDiscountEndDate,
    }
  }

  const calculateSumAssuredAndPremiumForSelectedRiders = (params) => {
    const {
      riders,
      validatedSumAssured,
      basicPremium,
      riderPremiumRates,
      riderOccupationFactors,
      modelFactor,
      basicPlan,
      userAgentType,
      riderPayerOccupationFactors,
      loan,
      sumAssuredContract,
    } = params
    const totalPremium = hasRidersNeedToCalculateFromTotalPremiumOrAnnualPremium(riders)
      ? preCalculateTotalPremium(params)
      : 0

    return riders.map((rider) => {
      if (!rider.isSelected) {
        return rider
      }

      const sumAssured = riderService.getRiderSumAssured(
        rider,
        validatedSumAssured.value,
        basicPremium,
        basicPlan,
        userAgentType,
        totalPremium,
        sumAssuredContract
      )
      const premium = riderService.calculateRiderPremium(rider, {
        riderPremiumRate: riderPremiumRates[rider.code],
        riderSumAssured: sumAssured,
        occupationFactor: riderOccupationFactors[rider.code],
        basicPremium,
        modelFactor: getFactor(basicPlan, modelFactor),
        modelFactorPeriod: modelFactor.periods,
        payerOccupationFactor: riderPayerOccupationFactors[rider.code],
        loan: loan,
        userAgentType,
        riderPremiumRates,
      })

      // TODO: Test this once module requirement is clear
      if (_.get(rider, 'selectedPlan.modules')) {
        const modules = rider.selectedPlan.modules.map((riderModule) => {
          const modulePremium = riderService.calculateRiderPremium(rider, {
            riderPremiumRate: riderPremiumRates[riderModule.code],
            riderSumAssured: sumAssured,
            occupationFactor: riderOccupationFactors[rider.code],
            basicPremium,
            modelFactor: getFactor(basicPlan, modelFactor),
            modelFactorPeriod: modelFactor.periods,
            payerOccupationFactor: riderPayerOccupationFactors[rider.code] || 1,
            userAgentType,
            riderPremiumRates,
          })
          return isNotNil(modulePremium.fullPremium)
            ? { ...riderModule, premium: modulePremium.premium, fullPremium: modulePremium.fullPremium }
            : { ...riderModule, premium: modulePremium }
        })
        const selectedPlanState = { selectedPlan: { ...rider.selectedPlan, modules } }
        return isNotNil(premium.fullPremium)
          ? { ...rider, sumAssured, premium: premium.premium, fullPremium: premium.fullPremium, ...selectedPlanState }
          : { ...rider, sumAssured, premium, ...selectedPlanState }
      }

      if (_.get(rider, 'options[0].type') === VALUES.OPTION_ANY_VALUE_PLAN_GROUP) {
        const riderUpdateSA = { ...rider, sumAssured }
        const selectedPlanGroup = calculateRiderPremiumSelectedPlanGroup(
          riderPremiumRates,
          riderUpdateSA,
          riderOccupationFactors,
          basicPremium,
          getFactor(basicPlan, modelFactor),
          modelFactor.periods,
          riderPayerOccupationFactors,
          loan,
          userAgentType
        )
        return isNotNil(premium.fullPremium)
          ? {
              ...rider,
              sumAssured,
              premium: premium.premium,
              fullPremium: premium.fullPremium,
              selectedPlanGroup,
            }
          : { ...rider, sumAssured, premium, selectedPlanGroup }
      }

      return isNotNil(premium.fullPremium)
        ? { ...rider, sumAssured, premium: premium.premium, fullPremium: premium.fullPremium }
        : { ...rider, sumAssured, premium }
    })
  }

  const preCalculateTotalPremium = (params) => {
    const {
      riders,
      validatedSumAssured,
      basicPremium,
      annualBasicPremium,
      riderPremiumRates,
      riderOccupationFactors,
      modelFactor,
      basicPlan,
      userAgentType,
      riderPayerOccupationFactors,
      loan,
    } = params
    const isNeedAnnualTotalPremium = hasRidersNeedToCalculateFromAnnualTotalPremium(riders)
    const _basicPremium = isNeedAnnualTotalPremium ? annualBasicPremium : basicPremium
    const _modelFactor = isNeedAnnualTotalPremium ? VALUES.MODEL_FACTOR_ANNUAL : modelFactor.factor
    const _modelFactorPeriod = isNeedAnnualTotalPremium ? VALUES.MODEL_FACTOR_ANNUAL : modelFactor.periods

    const updatedRiders = riders.map((rider) => {
      if (!rider.isSelected) {
        return rider
      }
      const sumAssured = riderService.getRiderSumAssured(
        rider,
        validatedSumAssured.value,
        _basicPremium,
        basicPlan,
        userAgentType,
        loan
      )
      const premium = riderService.calculateRiderPremium(rider, {
        riderPremiumRate: riderPremiumRates[rider.code],
        riderSumAssured: sumAssured,
        occupationFactor: riderOccupationFactors[rider.code],
        basicPremium: _basicPremium,
        modelFactor: _modelFactor,
        modelFactorPeriod: _modelFactorPeriod,
        payerOccupationFactor: riderPayerOccupationFactors[rider.code],
        loan: loan,
        userAgentType,
        riderPremiumRates,
      })

      return isNotNil(premium.fullPremium)
        ? { ...rider, sumAssured, premium: premium.premium, fullPremium: premium.fullPremium }
        : { ...rider, sumAssured, premium }
    })

    const riderPremium = totalRiderPremium(updatedRiders)
    return calculateTotalPremium(_basicPremium, riderPremium)
  }

  const calculateBasicPremiumAndAnnualBasicPremium = async (basicPremiumParams) => {
    const { modelFactor } = basicPremiumParams
    const basicPremium = await basicPlanPremiumService.calculateBasicPremium(basicPremiumParams)
    const annualBasicPremium =
      modelFactor === VALUES.MODEL_FACTOR_ANNUAL
        ? basicPremium
        : await basicPlanPremiumService.calculateBasicPremium({
            ...basicPremiumParams,
            modelFactor: VALUES.MODEL_FACTOR_ANNUAL,
          })
    const fullBasicPremium = await basicPlanPremiumService.calculateBasicPremium({
      ...basicPremiumParams,
      campaignDiscount: 0,
    })
    const fullAnnualBasicPremium = await basicPlanPremiumService.calculateBasicPremium({
      ...basicPremiumParams,
      campaignDiscount: 0,
      modelFactor: VALUES.MODEL_FACTOR_ANNUAL,
    })

    return { basicPremium, annualBasicPremium, fullBasicPremium, fullAnnualBasicPremium }
  }

  const getUpdatedRidersSAFromAnnualBasicPremium = async (basicPremiumParams) => {
    const {
      riders,
      displayProductQuery,
      sumAssured,
      premiumRate,
      natureOfDutyCodes,
      discount,
      rateUnit,
      modelFactor,
      annualBasicPremium,
      loan,
      campaignDiscount,
    } = basicPremiumParams

    const unresolvedPromises = riders.map(async (rider) => {
      if (_.get(rider, 'options.0.source') === VALUES.SOURCE_TOTAL_BASIC_ANNUAL_PREMIUM_WITH_LIMITED_RANGE)
        if (sumAssured > rider.defaultSumAssured) {
          const result = await calculateBasicPremiumAndAnnualBasicPremium({
            displayProductQuery,
            sumAssured: rider.defaultSumAssured,
            natureOfDutyCodes,
            premiumRate: premiumRate,
            discount,
            rateUnit,
            modelFactor: modelFactor.factor,
            loan,
            campaignDiscount: campaignDiscount,
          })

          rider.sumAssured = result.annualBasicPremium
        } else {
          rider.sumAssured = annualBasicPremium
        }
      return rider
    })

    return await Promise.all(unresolvedPromises)
  }

  return {
    _getBasicPlanDiscountRates,
    getBasicPlanDiscountRates,
    getBasicPlanCampaignDiscountRate,
    _getBasicPlanDiscountRate,
    getBasicPlanDiscountRate,
    checkWaiveAnnualBasicPremiumForReplaceWording,
    isCheckBasicCampaignDiscountActive,
    _getDefaultSelectedRiders,
    getDefaultSelectedRiders,
    getBasicPlanCampaignDiscount,
    getBasicPlanRateUnit,
    calculateCoveragePlanFromBasicPlanChange,
    calculateCoveragePlanFromSumAssured,
    calculateCoveragePlanFromPremium,
    populateInitialCoveragePlan,
    getUpdatedRidersSAFromAnnualBasicPremium,
  }
}
